import { Label } from '@trellis/roots/label'
import { FormDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Typography } from '@trellis/roots/typography'
import { useState } from 'react'
import { toast } from 'sonner'

import { useInvoicesQuery, useLinkReissuedInvoiceMutation } from '@/hooks/useInvoices'
import { formatCalendarDate, formatCurrency } from '@/lib/format'

interface LinkReissuedInvoiceDialogProps {
  invoice: { id: string; invoiceNumber: string; clientId: string } | null
  onOpenChange: (open: boolean) => void
}

/**
 * Records, after the fact, which existing invoice replaced a cancelled one. Changes
 * nothing but the link: statuses, dates, reminders and expenses are left as they are.
 */
export function LinkReissuedInvoiceDialog({
  invoice,
  onOpenChange,
}: LinkReissuedInvoiceDialogProps) {
  const open = !!invoice
  const [replacementId, setReplacementId] = useState('')
  const linkMutation = useLinkReissuedInvoiceMutation()
  const { currentPageData, isLoading } = useInvoicesQuery(
    { clientId: invoice?.clientId, limit: 100, archived: true },
    { enabled: open },
  )

  const candidates = (currentPageData?.data ?? []).filter(
    (candidate) =>
      candidate.id !== invoice?.id &&
      candidate.status !== 'CANCELLED' &&
      candidate.status !== 'VOID',
  )

  function handleConfirm() {
    if (!invoice || !replacementId) return
    linkMutation.mutate(
      { id: invoice.id, invoiceId: replacementId },
      {
        onSuccess: () => {
          onOpenChange(false)
          toast.success('Re-issued invoice linked')
        },
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error ? caughtError.message : 'Failed to link invoice'
          toast.error('Failed to link invoice', { description: message })
        },
      },
    )
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Link re-issued invoice"
      description={`Choose the invoice that replaced ${invoice?.invoiceNumber}. Only the link is recorded; neither invoice is changed.`}
      submitLabel="Link invoice"
      pendingLabel="Linking..."
      isPending={linkMutation.isPending}
      submitDisabled={!replacementId}
      onSubmit={handleConfirm}
      onReset={() => setReplacementId('')}
    >
      <div className="space-y-2">
        <Label htmlFor="reissuedAsInvoice">Replacement invoice</Label>
        <Select
          value={replacementId}
          onValueChange={(nextId) => {
            if (nextId) setReplacementId(nextId)
          }}
        >
          <SelectTrigger id="reissuedAsInvoice" disabled={isLoading}>
            <SelectValue placeholder="Select an invoice" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.invoiceNumber} · {formatCalendarDate(candidate.issueDate)} ·{' '}
                {formatCurrency(candidate.total)} · {candidate.status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!isLoading && candidates.length === 0 ? (
          <Typography variant="body-sm" color="muted">
            This client has no other invoices that can be linked.
          </Typography>
        ) : null}
      </div>
    </FormDialog>
  )
}
